/**
 * BrioClampText -- line-clamped text with expandable toggle (extra).
 *
 * Clamps content to a configurable number of lines with a subtle gradient
 * fade. Clicking the toggle button reveals the full content. Supports both
 * auto-rendered toggle and consumer-provided toggle markup.
 *
 * Usage (auto-rendered toggle):
 *   <brio-clamp-text lines="3">
 *       <p>Long text content that may need clamping...</p>
 *   </brio-clamp-text>
 *
 *   The element wraps content in .clamp-text-content and appends a
 *   .clamp-text-toggle button. Set lines="N" to clamp to N lines.
 *
 * Usage (consumer-provided toggle):
 *   <brio-clamp-text lines="3">
 *       <p>Long text...</p>
 *       <button class="button is-sm is-primary" data-clamp-toggle>Read more</button>
 *   </brio-clamp-text>
 *
 *   If a [data-clamp-toggle] element exists in the light DOM, the component
 *   uses it instead of auto-rendering. This lets consumers fully style
 *   the toggle (button classes, icon, text) while the component handles
 *   the ARIA state and click behavior.
 *
 * Usage (class-only, no JS):
 *   The CSS in clamp-text.css works standalone. For class-only usage without
 *   the custom element, consumers must manage aria-expanded and toggle text
 *   themselves. The CSS responds to [aria-expanded] attribute.
 *
 * Attributes:
 *   lines      -- Number of lines to clamp to (default: 3). Sets --clamp-text-lines.
 *   expanded   -- Initial state. "true" starts expanded, "false" starts clamped.
 *
 * Styling:
 *   The auto-rendered toggle receives class "clamp-text-toggle". Consumers
 *   can override with their own classes via the toggle-class attribute:
 *   <brio-clamp-text lines="3" toggle-class="button is-sm is-primary">
 *
 * ARIA:
 *   The component manages aria-expanded on the host and aria-controls on
 *   the toggle (pointing to the content's ID). Screen readers announce
 *   the expanded/collapsed state correctly.
 *
 * Keyboard:
 *   Toggle is keyboard-reachable via Tab and activated with Space/Enter.
 *   This is handled automatically by using a native <button>.
 */
import { BrioBase } from '../brio-base.js';

const DEFAULT_LINES = 3;
const DEFAULT_READ_MORE = '+ Read more';
const DEFAULT_READ_LESS = '- Read less';

class BrioClampText extends BrioBase {
    static tag = 'brio-clamp-text';
    static {
        customElements.define(BrioClampText.tag, BrioClampText);
    }

    #contentId;
    #toggle;
    #onClick;
    #animation;

    constructor() {
        super();
        this.#contentId = `clamp-${Math.random().toString(36).slice(2)}`;
    }

    connectedCallback() {
        // Apply lines attribute to --clamp-text-lines CSS var
        this.#applyLines();

        // Apply initial expanded state
        this.#applyExpanded();

        // Set up content wrapper and toggle
        this.#ensureDom();

        // Set up click handler
        this.#onClick = (e) => {
            const toggle = e.target.closest('[data-clamp-toggle]');
            if (!toggle) return;
            e.preventDefault();
            this.#toggleExpanded();
        };
        this.addEventListener('click', this.#onClick);

        // Handle keyboard for the auto-generated toggle
        this.#toggle?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.#toggleExpanded();
            }
        });
    }

    disconnectedCallback() {
        if (this.#onClick) {
            this.removeEventListener('click', this.#onClick);
        }
        this.#toggle?.removeEventListener('keydown', () => {});
    }

    /*
     * Observe attribute changes for lines and expanded.
     * These can be set/changed dynamically after initial render.
     */
    static get observedAttributes() {
        return ['lines', 'expanded'];
    }

    attributeChangedCallback(name, _oldVal, _newVal) {
        if (name === 'lines') {
            this.#applyLines();
        } else if (name === 'expanded') {
            this.#applyExpanded();
        }
    }

    #applyLines() {
        const lines = this.getAttribute('lines') || DEFAULT_LINES;
        this.style.setProperty('--clamp-text-lines', lines);
    }

    #applyExpanded() {
        const expanded = this.getAttribute('expanded') === 'true';
        this.setAttribute('aria-expanded', String(expanded));
        this.#updateToggleText();
    }

    #ensureDom() {
        // Find or create content wrapper
        let content = this.querySelector('.clamp-text-content');
        if (!content) {
            // Wrap all child elements (except existing toggle) in content wrapper
            const children = Array.from(this.children).filter(
                (c) => !c.hasAttribute('data-clamp-toggle')
            );
            content = document.createElement('div');
            content.className = 'clamp-text-content';
            content.id = this.#contentId;
            for (const child of children) {
                content.appendChild(child);
            }
            this.appendChild(content);
        }

        // Find existing toggle or create one
        let toggle = this.querySelector('[data-clamp-toggle]');
        if (!toggle) {
            toggle = this.#createToggle();
        }

        if (toggle && toggle !== this.lastElementChild) {
            this.appendChild(toggle);
        }

        this.#toggle = toggle;

        // Set up aria-controls on toggle
        toggle.setAttribute('aria-controls', this.#contentId);

        // Sync toggle state with host
        this.#updateToggleText();
    }

    #createToggle() {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'button is-sm is-ghost clamp-text-toggle';
        toggle.setAttribute('data-clamp-toggle', '');

        // Allow consumer to override button classes
        const toggleClass = this.getAttribute('toggle-class');
        if (toggleClass) {
            toggle.className = toggleClass;
        }

        return toggle;
    }

    #toggleExpanded() {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        const content = this.querySelector('.clamp-text-content');

        if (isExpanded) {
            this.#collapse(content);
        } else {
            this.#expand(content);
        }

        this.#updateToggleText();

        // Emit event for parent components to listen for
        this.emit('clamp-toggle', { expanded: !isExpanded });
    }

    /*
     * Smooth expand/collapse, in the spirit of the <details> widget.
     * Web Animations drives the box height between measured sizes; the
     * fade overlay is plain CSS (an opacity transition on a ::after
     * overlay), so the fade works the same with or without this JS.
     *
     * Expand flips the state first, then animates from the clamped
     * height to the natural height: text reveals progressively as the
     * box grows.
     *
     * Collapse flips aria-expanded at once (assistive tech hears the
     * change immediately), but a data-collapsing attribute holds the
     * expanded rendering while the box shrinks -- so text crops
     * progressively instead of vanishing at the click. The line clamp
     * (and its ellipsis) re-applies at the animation's final height,
     * landing inside the faded tail where the swap can't be seen.
     * The clamped height is measured by toggling the clamp within a
     * single task -- the browser never paints between the steps.
     *
     * Browsers without Web Animations, or with reduced motion
     * requested, keep the instant toggle. Both sequences run inside
     * the click's task, so no intermediate state ever paints.
     */
    #expand(content) {
        const from = content?.getBoundingClientRect().height;
        this.setAttribute('aria-expanded', 'true');
        const to = content?.getBoundingClientRect().height;
        this.removeAttribute('data-collapsing');
        this.#animateHeight(content, from, to);
    }

    #collapse(content) {
        const from = content?.getBoundingClientRect().height;
        this.setAttribute('aria-expanded', 'false');
        const to = content?.getBoundingClientRect().height;
        // Hold the expanded rendering (display: block) while the box
        // shrinks; clamp re-applies when the attribute comes off.
        this.setAttribute('data-collapsing', '');
        this.#animateHeight(content, from, to);
    }

    #animateHeight(content, from, to) {
        const reduce =
            !content ||
            typeof content.animate !== 'function' ||
            matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Instant path: state already flipped, nothing left to animate.
        // Clear the collapse hold too, so a stale one can't pin the
        // expanded rendering.
        if (reduce || from === to) {
            this.removeAttribute('data-collapsing');
            return;
        }

        const { duration, easing } = this.#motionTokens();

        // Clip during the animation in both directions; the expanded
        // state is overflow: visible and would show the tail early, and
        // an expand interrupted by a collapse strands the inline clip,
        // so it is always cleared on finish.
        content.style.overflow = 'hidden';

        this.#animation?.cancel();
        this.#animation = content.animate(
            { height: [`${from}px`, `${to}px`] },
            { duration, easing, fill: 'both' }
        );
        this.#animation.onfinish = () => {
            this.#animation?.cancel();
            this.#animation = null;
            content.style.overflow = '';
            this.removeAttribute('data-collapsing');
        };
    }

    /* Motion tokens live in CSS; read them so JS and CSS move together
       -- including the reduced-motion zeroing of --duration-normal.
       Computed values serialize as seconds ('.25s'), so convert. */
    #motionTokens() {
        const cs = getComputedStyle(this);
        const rawDuration = cs.getPropertyValue('--duration-normal').trim();
        const parsed = parseFloat(rawDuration);
        const duration = Number.isFinite(parsed)
            ? rawDuration.includes('ms')
                ? parsed
                : parsed * 1000
            : 250;
        const easing = cs.getPropertyValue('--ease-in-out').trim() || 'ease-in-out';
        return { duration, easing };
    }

    #updateToggleText() {
        if (!this.#toggle) return;

        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        const readMore = this.getAttribute('read-more') || DEFAULT_READ_MORE;
        const readLess = this.getAttribute('read-less') || DEFAULT_READ_LESS;

        // Update text for both auto-generated and slotted toggles
        // For slotted toggles, this updates the textContent while preserving
        // any HTML structure (like <span> wrappers) would need to be handled
        // by the consumer or we'd need to use innerHTML
        this.#toggle.textContent = isExpanded ? readLess : readMore;

        // Update aria-label for screen readers
        const action = isExpanded ? 'collapse' : 'expand';
        this.#toggle.setAttribute('aria-label', `${action} text`);
    }
}

export { BrioClampText };
